import {
  createContext,
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'

import {
  Field as BaseField,
  FieldContent,
  FieldDescription as BaseFieldDescription,
  FieldError as BaseFieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldTitle,
} from '@/components/ui/field'
import { cn } from '@/lib/utils'

type MessageKind = 'description' | 'error'

interface FieldMessages {
  describedBy: string | undefined
  register: (kind: MessageKind, id: string) => () => void
}

const FieldMessagesContext = createContext<FieldMessages | null>(null)

function Field({
  className,
  ...props
}: React.ComponentProps<typeof BaseField>) {
  const [descriptions, setDescriptions] = useState<Array<string>>([])
  const [errors, setErrors] = useState<Array<string>>([])

  const register = useCallback((kind: MessageKind, id: string) => {
    const update = kind === 'description' ? setDescriptions : setErrors
    update((ids) => [...ids, id])
    return () => update((ids) => ids.filter((entry) => entry !== id))
  }, [])

  const messages = useMemo(
    () => ({
      describedBy: [...descriptions, ...errors].join(' ') || undefined,
      register,
    }),
    [descriptions, errors, register],
  )

  return (
    <FieldMessagesContext.Provider value={messages}>
      <BaseField className={cn('gap-2', className)} {...props} />
    </FieldMessagesContext.Provider>
  )
}

function OutsideField({ children }: { children: React.ReactNode }) {
  return (
    <FieldMessagesContext.Provider value={null}>
      {children}
    </FieldMessagesContext.Provider>
  )
}

function useFieldMessage(kind: MessageKind, id: string, shown: boolean) {
  const register = useContext(FieldMessagesContext)?.register
  useLayoutEffect(() => {
    if (!register || !shown) return
    return register(kind, id)
  }, [register, kind, id, shown])
}

function useFieldDescribedBy(ownDescribedBy?: string) {
  const fieldDescribedBy = useContext(FieldMessagesContext)?.describedBy
  const ids = new Set(
    [ownDescribedBy, fieldDescribedBy].join(' ').split(' ').filter(Boolean),
  )
  return ids.size > 0 ? [...ids].join(' ') : undefined
}

function FieldDescription({
  id,
  ...props
}: React.ComponentProps<typeof BaseFieldDescription>) {
  const generatedId = useId()
  const descriptionId = id ?? generatedId
  useFieldMessage('description', descriptionId, true)
  return <BaseFieldDescription id={descriptionId} {...props} />
}

function FieldError({
  id,
  children,
  errors,
  ...props
}: React.ComponentProps<typeof BaseFieldError>) {
  const generatedId = useId()
  const errorId = id ?? generatedId
  const shown =
    Boolean(children) || (errors?.some((error) => error?.message) ?? false)
  useFieldMessage('error', errorId, shown)
  return (
    <BaseFieldError id={errorId} errors={errors} {...props}>
      {children}
    </BaseFieldError>
  )
}

export {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldTitle,
  OutsideField,
  useFieldDescribedBy,
}
